/**
 * @file features/scheduleReminders/desktopScheduleSync.ts
 * @brief Desktop app only: scheduled meetings and sessions → an alarm at the
 *        start time and an event in the Mac calendar.
 *
 * @details
 * DES 09-10-2026: the phone's alarm + calendar feature, "desktop par bhi".
 * The phone twin is FRONTEND/src/features/scheduleReminders/scheduleReminders.ts;
 * the rules are the same:
 *  - Items: GET /api/meetings?scope=upcoming (status scheduled) and
 *    GET /api/auth/scheduled-sessions (pending / reschedule_requested), start
 *    still ahead, next 30 days.
 *  - Alarm: the whole list goes to the shell (DESKTOP/src/alarms.js), which
 *    rings at the start in our Signature sound while the app runs; a click
 *    opens Scheduled Meetings (meeting) or Home (session).
 *  - Calendar (Mac): only when THIS user connected it ("From your calendar",
 *    useDesktopCalendar's per-user flag), with a reminder 10 min before. A
 *    meeting whose emailed invite (same title and start) is already in the
 *    calendar is skipped and our copy removed; an event the user deleted is
 *    never added back; cancelled / moved items are removed / updated.
 *
 * Runs in the main window only (DesktopScheduleBridge). In a browser tab the
 * shell is absent and nothing here runs.
 *
 * @author DES
 * @date 09-10-2026
 */

import { apiGet } from "@/shared/api/client";
import { desktopShell, type DesktopAlarm } from "@/shared/desktop/desktopShell";
import { listScheduledMeetings } from "@/features/employee-boardroom/scheduledMeetingsApi";

const HORIZON_MS = 30 * 24 * 60 * 60 * 1000;
const MEETING_DEFAULT_MIN = 60; // the emailed invite's default too
const SESSION_DEFAULT_MIN = 30;
const CALENDAR_REMIND_MIN = 10;
const SESSION_LIVE = new Set(["pending", "reschedule_requested"]);

const STORE_KEY = (uid: string) => `vz.scheduleSync.${uid}`;
/** Same flag as useDesktopCalendar ("From your calendar" → Connect). */
const CONNECTED_KEY = (uid: string) => `vz.calendar.connected.${uid}`;

export type ScheduledItem = {
  key: string;
  kind: "meeting" | "session";
  title: string;
  body: string;
  startMs: number;
  endMs: number;
  /** Page the alarm click opens. */
  path: string;
};

type CalendarRecord = { eventId: string | null; sig: string; skip?: "removed" | "duplicate" };
type Store = Record<string, CalendarRecord>;

function readStore(uid: string): Store {
  try {
    return JSON.parse(window.localStorage.getItem(STORE_KEY(uid)) || "{}") as Store;
  } catch {
    return {};
  }
}

function writeStore(uid: string, s: Store) {
  try {
    window.localStorage.setItem(STORE_KEY(uid), JSON.stringify(s));
  } catch {
    // Storage blocked: the next sync rebuilds what it can.
  }
}

function isConnected(uid: string): boolean {
  try {
    return window.localStorage.getItem(CONNECTED_KEY(uid)) === "1";
  } catch {
    return false;
  }
}

const langLabel = (code?: string | null) => (code || "").split("-")[0].toUpperCase();

type SessionRow = {
  id?: string | number;
  scheduled_at?: string | null;
  status?: string | null;
  time_status?: string | null;
  person_b_name?: string | null;
  source_language?: string | null;
  target_language?: string | null;
};

/** This user's upcoming meetings and sessions, soonest first. */
export async function fetchScheduledItems(paths: {
  meeting: string;
  session: string;
}): Promise<ScheduledItem[]> {
  return (await fetchScheduledOverview(paths)).items;
}

const MISSED_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * The upcoming list plus the interpretation sessions missed in the last
 * 24 h, from the same two requests. The Inbox (DES 12-10-2026) shows both;
 * the desktop alarms only need `items`. Same as the phone's
 * fetchScheduledOverview (FRONTEND scheduleReminders.ts).
 */
export async function fetchScheduledOverview(paths: {
  meeting: string;
  session: string;
}): Promise<{ items: ScheduledItem[]; missedRecent: number }> {
  const now = Date.now();
  let missedRecent = 0;
  const [meetings, sessionsRaw] = await Promise.all([
    listScheduledMeetings("upcoming"),
    apiGet<{ sessions?: SessionRow[] }>("/api/auth/scheduled-sessions"),
  ]);
  const out: ScheduledItem[] = [];

  for (const m of meetings) {
    const startMs = Date.parse(m.scheduled_at);
    if (!Number.isFinite(startMs) || m.status !== "scheduled") continue;
    out.push({
      key: `meeting.${m.id}`,
      kind: "meeting",
      title: m.title || "Meeting",
      body: "Your meeting is starting",
      startMs,
      endMs: startMs + (m.duration_minutes || MEETING_DEFAULT_MIN) * 60_000,
      path: paths.meeting,
    });
  }

  for (const s of sessionsRaw?.sessions ?? []) {
    const startMs = Date.parse(s?.scheduled_at || "");
    if (!s?.id || !Number.isFinite(startMs)) continue;
    if (s.time_status === "missed" && s.status !== "completed" && now - startMs < MISSED_WINDOW_MS) {
      missedRecent += 1;
      continue;
    }
    if (s.status && !SESSION_LIVE.has(s.status)) continue;
    const pair =
      s.source_language && s.target_language
        ? `${langLabel(s.source_language)} → ${langLabel(s.target_language)}`
        : "";
    out.push({
      key: `session.${s.id}`,
      kind: "session",
      title: s.person_b_name ? `Interpretation with ${s.person_b_name}` : "Interpretation session",
      body: [pair, "Your session is starting"].filter(Boolean).join(" · "),
      startMs,
      endMs: startMs + SESSION_DEFAULT_MIN * 60_000,
      path: paths.session,
    });
  }

  const items = out
    .filter((i) => i.startMs > now && i.startMs < now + HORIZON_MS)
    .sort((a, b) => a.startMs - b.startMs);
  return { items, missedRecent };
}

const sameStart = (a: number, b: number) => Math.abs(a - b) < 60_000;
const norm = (t: string) => t.trim().toLowerCase();

async function syncCalendar(uid: string, items: ScheduledItem[]) {
  const shell = desktopShell();
  if (!shell?.calendarSave || !shell.calendarRemove || !shell.calendarStatus) return;
  const os = await shell.calendarStatus().catch(() => "unsupported" as const);
  if (os !== "authorized" && os !== "writeOnly") return;

  const store = readStore(uid);
  const live = new Set(items.map((i) => i.key));
  for (const [key, rec] of Object.entries(store)) {
    if (live.has(key)) continue;
    if (rec.eventId) await shell.calendarRemove(rec.eventId).catch(() => false);
    delete store[key];
  }

  if (isConnected(uid)) {
    for (const i of items) {
      const rec = store[i.key];
      if (rec?.skip === "removed") continue;
      const sig = `${i.title}|${i.startMs}|${i.endMs}`;

      if (i.kind === "meeting" && os === "authorized" && shell.calendarEvents) {
        const around = await shell
          .calendarEvents(i.startMs - 60_000, i.startMs + 60_000)
          .catch(() => []);
        const invite = around.find(
          (e) =>
            !e.cancelled &&
            sameStart(e.startMs, i.startMs) &&
            norm(e.title) === norm(i.title) &&
            !(rec?.eventId && e.id.startsWith(`${rec.eventId}@`)),
        );
        if (invite) {
          if (rec?.eventId) await shell.calendarRemove(rec.eventId).catch(() => false);
          store[i.key] = { eventId: null, sig, skip: "duplicate" };
          continue;
        }
      }

      if (rec?.eventId && rec.sig === sig) continue;
      const id = await shell
        .calendarSave({
          eventId: rec?.eventId ?? null,
          title: i.title,
          startMs: i.startMs,
          endMs: i.endMs,
          notes: "Added by Verblizr",
          alarmMinutes: [CALENDAR_REMIND_MIN],
        })
        .catch(() => undefined);
      if (id === undefined) continue; // failed: try again next sync
      store[i.key] = id === null ? { eventId: null, sig, skip: "removed" } : { eventId: id, sig };
    }
  }
  writeStore(uid, store);
}

let running: Promise<void> | null = null;
let again = false;
let latest: { uid: string | null; paths: { meeting: string; session: string } } = {
  uid: null,
  paths: { meeting: "/", session: "/" },
};

/**
 * Bring the desktop's alarms and Mac calendar events in line with the server.
 * Serialised; a call during a run triggers one more with the newest user.
 */
export function syncDesktopSchedule(
  uid: string | null,
  paths: { meeting: string; session: string },
): Promise<void> {
  latest = { uid, paths };
  if (running) {
    again = true;
    return running;
  }
  running = (async () => {
    try {
      do {
        again = false;
        const { uid: forUid, paths: forPaths } = latest;
        const shell = desktopShell();
        if (!shell?.setAlarms) return;
        if (!forUid) {
          // Signed out: nothing of the last user may ring.
          await shell.setAlarms([]);
          continue;
        }
        const items = await fetchScheduledItems(forPaths);
        const alarms: DesktopAlarm[] = items.map((i) => ({
          id: i.key,
          fireMs: i.startMs,
          title: i.title,
          body: i.body,
          path: i.path,
        }));
        await shell.setAlarms(alarms);
        await syncCalendar(forUid, items);
      } while (again);
    } catch (e) {
      // Quiet: offline or a failed list keeps the alarms already set.
      console.warn("[scheduleReminders] desktop sync failed", e);
    } finally {
      running = null;
    }
  })();
  return running;
}
